import PropTypes from 'prop-types';
import { graphql } from '@apollo/client/react/hoc';
import Head from 'next/head';
import Router from 'next/router';
import { Formik } from 'formik';
import compose from 'lodash/flowRight';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import Typography from '@material-ui/core/Typography';
import GroupForm, { groupSchema } from '../../components/groups/form';
import { createGroupMutation } from '../../apollo/groups';
import { primaryCityQuery } from '../../apollo/cities';
import { meQuery } from '../../apollo/auth';
import { myGroupsQuery } from '../../apollo/groups';
import consumeAppContext from '../../consumers/app';
import LoginRequiredNotice from '../../components/login-required-notice';
import { buildTitle } from '../../utils/layout';
import Loading from '../../components/loading';
import ErrorMessage from '../../components/error-message';
import config from '../../config';
import Main from '../../components/main';

const NewGroup = (props) => {
  const {
    primaryCity,
    primaryCityLoading,
    primaryCityError,
    createGroup,
    isLoggedIn,
  } = props;

  if (primaryCityLoading) {
    return <Loading />;
  }
  if (primaryCityError) {
    return (
      <ErrorMessage
        message={primaryCityError.message}
        error={primaryCityError}
      />
    );
  }

  return (
    <Main>
      <Head>
        <title>{buildTitle('Create Group')}</title>
        <link rel="canonical" href={`${config().universal.baseUri}/groups/new`} />
      </Head>

      <Typography variant="h1">Create Group</Typography>

      {!isLoggedIn && <LoginRequiredNotice returnTo="/groups/new" action="create a group" />}

      <Formik
        initialValues={{
          public: true,
          discoverable: true,
          createEventPermission: 'OWNER',
          createPostPermission: 'OWNER',
          uploadImagePermission: 'MEMBER',
          cityId: get(primaryCity, 'id'),
        }}
        validationSchema={groupSchema({ editing: false })}
        onSubmit={(values, actions) => {
          createGroup({
            variables: {
              data: values,
            },
          }).then(({ data: { createGroup: group } }) => {
            // actions.setSubmitting(false);
            Router.push(
              '/groups/[slug]',
              `/groups/${group.slug}`
            );
          }).catch((error) => {
            actions.setSubmitting(false);
            console.log(error);
            // actions.setErrors();
            // actions.setStatus();
          });
        }}
      >
        {(props) => <GroupForm {...props} disabled={!isLoggedIn} />}
      </Formik>
    </Main>
  );
}

NewGroup.propTypes = {
  client: PropTypes.object,
  isLoggedIn: PropTypes.bool,
  primaryCity: PropTypes.object,
  primaryCityLoading: PropTypes.bool,
  primaryCityError: PropTypes.object,
  createGroup: PropTypes.func
};

export default compose(
  consumeAppContext,
  graphql(createGroupMutation, {
    name: 'createGroup',
    options: {
      update: (proxy, { data: { createGroup: newGroup } }) => {
        // add to auth group list
        try {
          const data = proxy.readQuery({
            query: meQuery
          })
          proxy.writeQuery({
            query: meQuery,
            data: {
              ...data,
              me: {
                ...data.me,
                groupMembers: [...data.me.groupMembers, newGroup.membership]
              }
            }
          })
        } catch (e) {
          // do nothing
        }

        // update group dropdown
        try {
          const data = proxy.readQuery({
            query: myGroupsQuery
          })
          proxy.writeQuery({
            query: myGroupsQuery,
            data: {
              ...data,
              me: {
                ...data.me,
                groups: sortBy([...data.me.groups, newGroup], 'name')
              }
            }
          })
        } catch (e) {
          // do nothing
        }
      }
    }
  }),
  graphql(primaryCityQuery, {
    props: ({ data: { me, loading, error } }) => {
      return {
        primaryCity: get(me, 'primaryCity'),
        primaryCityLoading: loading,
        primaryCityError: error
      };
    },
  }),
)(NewGroup);
